/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useState, useEffect } from 'react';
import { biApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, RadialBarChart, RadialBar
} from 'recharts';
import {
  ChartBar, TrendUp, Users, Clock, Folders, Target, TestTube, CalendarBlank, Warning
} from '@phosphor-icons/react';

const COLORS = ['#2563eb', '#7c3aed', '#ea580c', '#059669', '#0284c7', '#e11d48', '#ca8a04', '#6366f1', '#14b8a6', '#f43f5e'];
const BILLABLE_COLORS = { billable: '#059669', nonBillable: '#94a3b8' };

function KpiCard({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <Card className="card-surface p-4 hover:shadow-md transition-shadow" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={20} weight="duotone" className="text-white" />
        </div>
        <div>
          <p className="text-2xl font-extrabold text-slate-900">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
          {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-slate-200 px-3 py-2">
      <p className="text-xs font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color }}>
          {p.name}: <span className="font-semibold">{typeof p.value === 'number' ? p.value.toFixed(1) : p.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function BIPage() {
  const [overview, setOverview] = useState(null);
  const [timeByProject, setTimeByProject] = useState([]);
  const [workload, setWorkload] = useState([]);
  const [trends, setTrends] = useState([]);
  const [trendMonths, setTrendMonths] = useState('6');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [ovRes, tpRes, wlRes, trRes] = await Promise.all([
        biApi.overview(),
        biApi.timeByProject(),
        biApi.workload(),
        biApi.trends(parseInt(trendMonths)),
      ]);
      setOverview(ovRes.data);
      setTimeByProject(tpRes.data);
      setWorkload(wlRes.data);
      setTrends(trRes.data);
    } catch (e) {
      console.error('BI fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      biApi.trends(parseInt(trendMonths)).then(r => setTrends(r.data)).catch(() => {});
    }
  }, [trendMonths]);

  if (loading) {
    return (
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[...Array(5)].map((_, i) => <div key={i} className="h-20 bg-slate-200 rounded-lg animate-pulse" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-64 bg-slate-200 rounded-lg animate-pulse" />)}
        </div>
      </div>
    );
  }

  const completionData = overview ? [
    { name: 'Terminee', value: overview.tasks.closed, fill: '#059669' },
    { name: 'Restante', value: overview.tasks.total - overview.tasks.closed, fill: '#e2e8f0' },
  ].filter(d => d.value > 0) : [];

  const billablePie = timeByProject.length > 0 ? [
    { name: 'Facturable', value: timeByProject.reduce((s, p) => s + p.billable_days, 0), fill: BILLABLE_COLORS.billable },
    { name: 'Non-facturable', value: timeByProject.reduce((s, p) => s + (p.total_days - p.billable_days), 0), fill: BILLABLE_COLORS.nonBillable },
  ].filter(d => d.value > 0) : [];

  const workloadReady = workload.map(u => ({
    ...u,
    shortName: u.name?.split(' ').map(n => n[0]).join('').slice(0, 3) || '?',
  }));

  return (
    <div className="p-5 space-y-5 animate-fade-in" data-testid="bi-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChartBar size={22} weight="duotone" className="text-blue-600" />
          <h1 className="text-lg font-bold text-slate-900">Business Intelligence</h1>
        </div>
        <Badge className="bg-blue-50 text-blue-700 text-xs">
          Donnees en temps reel
        </Badge>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard icon={Folders} label="Projets actifs" value={overview?.projects?.active || 0}
          sub={`${overview?.projects?.total || 0} total`} color="bg-blue-600" testId="bi-kpi-projects" />
        <KpiCard icon={Target} label="Taches fermees" value={overview?.tasks?.closed || 0}
          sub={`${overview?.tasks?.completion_rate || 0}% completion`} color="bg-violet-600" testId="bi-kpi-tasks" />
        <KpiCard icon={Warning} label="En retard" value={overview?.tasks?.overdue || 0}
          sub="taches" color="bg-red-600" testId="bi-kpi-overdue" />
        <KpiCard icon={Clock} label="Jours ce mois" value={overview?.time?.this_month || 0}
          color="bg-orange-600" testId="bi-kpi-time" />
        <KpiCard icon={TestTube} label="Tests reussis" value={`${overview?.tests?.success_rate || 0}%`}
          sub={`${overview?.tests?.passed || 0}/${overview?.tests?.total || 0}`} color="bg-emerald-600" testId="bi-kpi-tests" />
      </div>

      {/* Charts Row 1: Time by Project + Billable Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="card-surface lg:col-span-2">
          <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Clock size={16} className="text-blue-600" />
              Repartition du temps par projet
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {timeByProject.length === 0 ? (
              <div className="h-56 flex items-center justify-center text-sm text-slate-400">Aucune donnee de temps</div>
            ) : (
              <div className="h-56" data-testid="bi-chart-time-project">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={timeByProject.slice(0, 10)} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="project_code" tick={{ fontSize: 10, fill: '#64748b' }} interval={0} angle={-20} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} label={{ value: 'Jours', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#94a3b8' } }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="billable_days" name="Facturable" fill="#059669" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="total_days" name="Total jours" fill="#2563eb" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="card-surface">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <TrendUp size={16} className="text-emerald-600" />
              Facturable / Non-fact.
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3 flex flex-col items-center">
            {billablePie.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-sm text-slate-400">Pas de donnees</div>
            ) : (
              <>
                <div className="h-48 w-full" data-testid="bi-chart-billable">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={billablePie} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value">
                        {billablePie.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex gap-4 mt-1">
                  <div className="text-center">
                    <p className="text-lg font-bold text-emerald-600">{billablePie[0]?.value?.toFixed(1) || 0}j</p>
                    <p className="text-[10px] text-slate-500">Facturable</p>
                  </div>
                  {billablePie[1] && (
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-400">{billablePie[1]?.value?.toFixed(1) || 0}j</p>
                      <p className="text-[10px] text-slate-500">Non-fact.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Trends + Workload */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Trends */}
        <Card className="card-surface">
          <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <TrendUp size={16} className="text-violet-600" />
              Tendances mensuelles
            </CardTitle>
            <Select value={trendMonths} onValueChange={setTrendMonths}>
              <SelectTrigger className="w-24 h-7 text-xs" data-testid="bi-trend-months">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 mois</SelectItem>
                <SelectItem value="6">6 mois</SelectItem>
                <SelectItem value="12">12 mois</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {trends.length === 0 ? (
              <div className="h-56 flex items-center justify-center text-sm text-slate-400">Pas de donnees</div>
            ) : (
              <div className="h-56" data-testid="bi-chart-trends">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trends} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="tasks_created" name="Creees" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="tasks_closed" name="Fermees" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="time_logged" name="Temps (j)" stroke="#ea580c" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Workload */}
        <Card className="card-surface">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Users size={16} className="text-orange-600" />
              Charge equipe (ce mois)
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {workloadReady.length === 0 ? (
              <div className="h-56 flex items-center justify-center text-sm text-slate-400">Aucun utilisateur</div>
            ) : (
              <div className="h-56" data-testid="bi-chart-workload">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={workloadReady.slice(0, 8)} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} width={55} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="active_tasks" name="Taches actives" fill="#7c3aed" radius={[0, 4, 4, 0]} barSize={14} />
                    <Bar dataKey="time_this_month" name="Temps (j)" fill="#2563eb" radius={[0, 4, 4, 0]} barSize={14} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Workload detail table */}
      <Card className="card-surface">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Users size={16} className="text-blue-600" />
            Detail charge par ressource
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full pro-table">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="px-4 py-2 text-left">Ressource</th>
                <th className="px-4 py-2 text-left">Role</th>
                <th className="px-4 py-2 text-center">Taches actives</th>
                <th className="px-4 py-2 text-center">Temps ce mois (j)</th>
                <th className="px-4 py-2 text-center">Statut</th>
              </tr>
            </thead>
            <tbody>
              {workloadReady.map((u, i) => (
                <tr key={u.user_id} className="border-b hover:bg-slate-50 transition-colors" data-testid={`bi-workload-row-${i}`}>
                  <td className="px-4 py-2 text-sm font-medium text-slate-900">{u.name}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{u.role}</td>
                  <td className="px-4 py-2 text-center">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-violet-50 text-violet-700 text-xs font-bold">{u.active_tasks}</span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className="inline-flex items-center justify-center w-12 h-7 rounded-lg bg-blue-50 text-blue-700 text-xs font-bold">{u.time_this_month}j</span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {u.absent ? (
                      <Badge className="bg-amber-100 text-amber-800 text-[10px]">{u.absence_reason || 'Absent'}</Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Disponible</Badge>
                    )}
                  </td>
                </tr>
              ))}
              {workloadReady.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400">Aucune donnee</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Time by project detail table */}
      <Card className="card-surface">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <CalendarBlank size={16} className="text-emerald-600" />
            Detail temps par projet
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full pro-table">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="px-4 py-2 text-left">Code</th>
                <th className="px-4 py-2 text-left">Projet</th>
                <th className="px-4 py-2 text-center">Saisies</th>
                <th className="px-4 py-2 text-center">Total (j)</th>
                <th className="px-4 py-2 text-center">Facturable (j)</th>
                <th className="px-4 py-2 text-center">Taux fact.</th>
              </tr>
            </thead>
            <tbody>
              {timeByProject.map((p, i) => {
                const rate = p.total_days > 0 ? Math.round(p.billable_days / p.total_days * 100) : 0;
                return (
                  <tr key={i} className="border-b hover:bg-slate-50 transition-colors" data-testid={`bi-time-row-${i}`}>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500">{p.project_code || '-'}</td>
                    <td className="px-4 py-2 text-sm font-medium text-slate-900">{p.project_name}</td>
                    <td className="px-4 py-2 text-center text-xs text-slate-600">{p.entries}</td>
                    <td className="px-4 py-2 text-center">
                      <span className="text-sm font-bold text-blue-700">{p.total_days}j</span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className="text-sm font-bold text-emerald-700">{p.billable_days}j</span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <Badge className={`text-[10px] ${rate >= 80 ? 'bg-emerald-100 text-emerald-700' : rate >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                        {rate}%
                      </Badge>
                    </td>
                  </tr>
                );
              })}
              {timeByProject.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">Aucune donnee de temps</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
